/**
 * Dialog — modal mínimo sin dependencias externas.
 *
 * Patrones:
 *  - `open`: si false no renderiza nada.
 *  - `onClose`: llamado al click backdrop o Escape (solo el diálogo de
 *    arriba, si hay uno abierto dentro de otro).
 *  - Bloquea scroll del body mientras está abierto.
 *  - `size` controla el ancho máximo. El body tiene scroll interno
 *    cuando el contenido excede la altura del viewport — el header
 *    y footer permanecen visibles (sticky-like via flex column).
 *
 * Para casos avanzados (focus trap, animaciones), migrar a Radix Dialog
 * cuando aparezca la necesidad.
 */

import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** Footer típicamente lleva los botones de acción primaria/secundaria. */
  footer?: ReactNode
  /** Ancho del modal. Default 'md' = max-w-lg (legacy). */
  size?: DialogSize
}

const SIZE_CLASSES: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

/** Diálogos abiertos, el último arriba. Un diálogo puede abrir otro (p.ej.
 * crear una metodología desde "Nueva plantilla"): Escape cierra solo el de
 * arriba, no toda la pila. */
const openDialogs: object[] = []

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: DialogProps) {
  // `onClose` suele ser una función nueva en cada render; el efecto de
  // abajo depende solo de `open` para no reordenar la pila al re-renderizar.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return
    const token = {}
    openDialogs.push(token)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openDialogs[openDialogs.length - 1] === token) {
        onCloseRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      openDialogs.splice(openDialogs.indexOf(token), 1)
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {/*
        Estructura flex column con max-h: viewport - padding del backdrop.
        Header y footer son shrink-0; el body crece y tiene overflow-y:auto.
        Garantiza que en pantallas pequeñas (1080p, etc.) el modal nunca
        excede el viewport — el contenido scrollea internamente.
      */}
      <div
        className={`w-full ${SIZE_CLASSES[size]} max-h-[calc(100vh-2rem)] flex flex-col rounded-md border border-line bg-surface shadow-lg`}
      >
        <header className="shrink-0 border-b border-line px-5 py-4">
          <h2 className="font-display text-lg text-ink">{title}</h2>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer && (
          <footer className="shrink-0 flex items-center justify-end gap-2 border-t border-line px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
